import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Chip, Collapse, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, INK_BLACK } from "../../../theme/tokens";
import { AffectedNurseRow } from "./AffectedNurseRow";
import { AlertDetailVisual } from "./AlertDetailVisual";
import { AlertType, type ProblemsAlert } from "./types";
import { AFFECTED_NURSES_DISPLAY_LIMIT, ALERT_TYPE_COLORS, ALERT_TYPE_I18N_KEYS, DETAIL_BG, keyboardClickProps, mergeNurses, type SubSection } from "./utils";

type Props = {
	alert: ProblemsAlert;
};

export const ProblemsCard: FC<Props> = memo(({ alert }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const toggle = useCallback(() => setExpanded((p) => !p), []);

	const sections = useMemo((): SubSection[] => {
		const result: SubSection[] = [];
		if (alert.overstaffed) result.push({ type: AlertType.Overstaffed, sub: alert.overstaffed });
		if (alert.mismatch) result.push({ type: AlertType.Mismatch, sub: alert.mismatch });
		if (alert.weeklyOvertime) result.push({ type: AlertType.WeeklyOvertime, sub: alert.weeklyOvertime });
		return result;
	}, [alert]);

	const mergedNurses = useMemo(() => mergeNurses(sections, t), [sections, t]);

	const gradientStops = useMemo(
		() =>
			sections.map((s, i) => {
				const color = ALERT_TYPE_COLORS[s.type].text;
				const start = (i / sections.length) * 100;
				const end = ((i + 1) / sections.length) * 100;
				return `${color} ${start}%, ${color} ${end}%`;
			}),
		[sections],
	);

	return (
		<Box
			sx={{
				bgcolor: "white",
				borderRadius: "8px",
				border: "1px solid",
				borderColor: "grey.200",
				overflow: "hidden",
				transition: "all 0.2s ease",
				boxShadow: expanded ? `0px 2px 8px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT)}` : "none",
				"&:hover": { borderColor: "grey.300", boxShadow: `0px 1px 4px ${alpha(INK_BLACK, 0.06)}` },
				position: "relative",
			}}
		>
			{/* Multi-tone left accent bar */}
			<Box
				sx={{
					position: "absolute",
					left: 0,
					top: 0,
					bottom: 0,
					width: 3,
					borderRadius: "8px 0 0 8px",
					background: `linear-gradient(to bottom, ${gradientStops.join(", ")})`,
				}}
			/>
			{/* Main row */}
			<Box
				onClick={toggle}
				{...keyboardClickProps(toggle)}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					px: 1,
					pl: 1.5,
					py: 1.5,
					"&:hover": { bgcolor: "grey.50" },
					transition: "background-color 0.15s",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flex: 1,
						minWidth: 0,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							fontWeight: 700,
						}}
					>
						{t("alerts.problems")}
					</Typography>
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{sections.map((s, i) => {
						const colors = ALERT_TYPE_COLORS[s.type];
						return (
							<Box
								key={s.type}
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								{i > 0 && <Box sx={{ width: 1, height: 20, bgcolor: "grey.200" }} />}
								<Chip
									size="small"
									label={`${s.sub.count} ${t(ALERT_TYPE_I18N_KEYS[s.type])}`}
									sx={{ bgcolor: colors.chipBg, color: colors.text, fontWeight: 600, fontSize: "0.7rem", height: 24 }}
								/>
							</Box>
						);
					})}
					<KeyboardArrowDownIcon
						sx={{
							transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.2s",
							color: "text.secondary",
							fontSize: 20,
						}}
					/>
				</Box>
			</Box>
			{/* Expanded details */}
			<Collapse in={expanded} unmountOnExit>
				<Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid", borderColor: "grey.100", bgcolor: DETAIL_BG }}>
					{/* Detail pills from all types */}
					<Typography
						variant="caption"
						sx={{
							fontWeight: 700,
							textTransform: "uppercase",
							color: "text.secondary",
							letterSpacing: 0.5,
						}}
					>
						{t("details")}
					</Typography>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							mt: 0.5,
							mb: 1.5,
						}}
					>
						{sections.map((s) => (
							<AlertDetailVisual key={s.type} alert={s.sub} typeColors={ALERT_TYPE_COLORS[s.type]} />
						))}
					</Box>

					{/* Merged affected staff */}
					{mergedNurses.length > 0 && (
						<>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 700,
									textTransform: "uppercase",
									color: "text.secondary",
									letterSpacing: 0.5,
								}}
							>
								{t("affected.staff")}
							</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 0.5,
									mt: 0.5,
								}}
							>
								{mergedNurses.slice(0, AFFECTED_NURSES_DISPLAY_LIMIT).map((nurse, idx) => (
									<AffectedNurseRow key={`${nurse.nurseId}-${idx}`} nurse={nurse} typeColors={ALERT_TYPE_COLORS[nurse.sourceType]} />
								))}
								{mergedNurses.length > AFFECTED_NURSES_DISPLAY_LIMIT && (
									<Typography
										variant="caption"
										sx={{
											color: "text.secondary",
										}}
									>
										{t("alerts.more.staff", { count: mergedNurses.length - AFFECTED_NURSES_DISPLAY_LIMIT })}
									</Typography>
								)}
							</Box>
						</>
					)}
				</Box>
			</Collapse>
		</Box>
	);
});
